import { useEffect } from 'react';

import { Loader2 } from 'lucide-react';
import { Controller } from 'react-hook-form';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { Button } from '@nesso/ui/primitives/button';
import { CheckboxField } from '@nesso/ui/primitives/checkbox-field';
import { CurrencyInputField } from '@nesso/ui/primitives/currency-input-field';
import { Field, FieldLabel, FieldSlot } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText,
} from '@nesso/ui/primitives/input-group';
import { SheetBody, SheetFooter } from '@nesso/ui/primitives/sheet';

import type { Service } from '@/entities/service-catalog';
import { stripNonDigits } from '@/shared/lib/format-utils';

import { useSaveServiceForm } from '../../../model/use-save-service-form';
import { SaveServiceCodePicker } from './save-service-sheet-form/save-service-code-picker';

type Props = {
    service?: Service;
    onCancel: () => void;
    onSaved: (code: string) => void;
    onPendingChange: (pending: boolean) => void;
};

// The hook lives here, not in `save-service-sheet.tsx`: Radix unmounts `SheetContent`'s children
// (this component included) once the sheet finishes closing, which is what discards an unsaved
// custom code — the outer `SaveServiceSheet` itself never unmounts while `open` toggles.
export function SaveServiceSheetForm({ service, onCancel, onSaved, onPendingChange }: Props) {
    const saveService = useSaveServiceForm({ service, onSuccess: onSaved });
    const { control } = saveService.form;

    useEffect(() => {
        onPendingChange(saveService.isPending);
        return () => onPendingChange(false);
    }, [saveService.isPending, onPendingChange]);

    return (
        <form onSubmit={saveService.onSubmit} className="flex min-h-0 flex-1 flex-col">
            <SheetBody className="flex flex-1 flex-col gap-6 overflow-y-auto">
                <Controller
                    control={control}
                    name="code"
                    render={({ field, fieldState }) => (
                        <SaveServiceCodePicker
                            value={field.value}
                            error={fieldState.error?.message ?? fieldState.invalid}
                            onSelect={saveService.selectCode}
                            // Editing never changes the code, so the trigger shows it and is
                            // disabled — a departure from the Figma Edit sheet, which draws an
                            // enabled picker.
                            disabled={saveService.isEdit}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="description"
                    render={({ field, fieldState }) => (
                        <InputField
                            {...field}
                            id="save-service-description"
                            label="Description"
                            description="This description will appear on superbills and client invoices"
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="makeDefault"
                    render={({ field }) => (
                        <CheckboxField
                            id="save-service-make-default"
                            label="Make this the default service"
                            checked={field.value}
                            disabled={service?.isDefault}
                            onCheckedChange={(value) => field.onChange(value === true)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="rate"
                    render={({ field, fieldState }) => (
                        <CurrencyInputField
                            {...field}
                            id="save-service-rate"
                            label="Default Rate"
                            error={fieldState.error?.message ?? fieldState.invalid}
                            // Figma 624:8286 / 631:20537: 137px at every width, not full width —
                            // 136px (`w-34`) is the nearest scale value. On the control, not the
                            // field, so the label and error message stay full width.
                            inputGroupClassName="w-34"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="duration"
                    render={({ field: { onChange, onBlur, value }, fieldState }) => (
                        <DurationField
                            value={value}
                            onChange={(next) => onChange(stripNonDigits(next))}
                            onBlur={onBlur}
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />
            </SheetBody>
            <SheetFooter>
                <Button
                    type="button"
                    variant="ghost"
                    disabled={saveService.isPending}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button type="submit" disabled={saveService.isPending}>
                    {saveService.isPending && <Loader2 className="animate-spin" />}
                    {saveService.isEdit ? 'Update Service' : 'Add Service'}
                </Button>
            </SheetFooter>
        </form>
    );
}

type DurationFieldProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    error?: boolean | string;
};

// No existing `*-field` wrapper offers a trailing text addon (only `CurrencyInputField`'s
// leading `$`), so this composes `Field` + `InputGroup` directly, the way `InputField` composes
// `Field` + `Input` — it is used once, so it stays local rather than becoming a primitive.
function DurationField({ value, onChange, onBlur, error }: DurationFieldProps) {
    const wiring = useFieldWiring({ id: 'save-service-duration', error });

    return (
        <Field data-invalid={wiring.dataInvalid}>
            <FieldLabel htmlFor={wiring.controlId}>Duration</FieldLabel>
            {/* Figma 624:8286 / 631:20537: 137px at every width, not full width — 136px (`w-34`)
                is the nearest scale value. On the control, not the field, so the label and error
                message stay full width. */}
            <InputGroup className="w-34">
                <InputGroupInput
                    {...wiring.controlProps}
                    inputMode="numeric"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                />
                <InputGroupAddon align="inline-end">
                    <InputGroupText>minutes</InputGroupText>
                </InputGroupAddon>
            </InputGroup>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
